import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { parseUtc } from '../lib/calendarDate';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Video, Users, ExternalLink, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sprint 1.0c V1.a-bis hotfix #5 (Bug I) — rich preview card for event_share DMs.
 *
 * The sender (ShareModal.handleSendDM) embeds a `[EVENT_SHARE:<event_id>]` marker
 * at the start of the message content. This component:
 *   1) fetches the event details from /api/calendar/events/{id}
 *   2) renders a rich preview (date pill, title, time, description, CTAs)
 *   3) gracefully falls back to plain text if the event has been deleted /
 *      the user has no visibility on it (404 from the API)
 */
const MessageEventPreview = ({ eventId, fallbackText }) => {
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchEvent = async () => {
      try {
        const { data } = await axios.get(`${API}/calendar/events/${eventId}`, { withCredentials: true });
        if (!cancelled) setEvent(data);
      } catch (e) {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchEvent();
    return () => { cancelled = true; };
  }, [eventId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-50 border border-purple-100 text-purple-700 text-xs" data-testid={`dm-event-loading-${eventId}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Chargement de l'événement…
      </div>
    );
  }

  if (failed || !event) {
    // Event deleted or no visibility → render plain link
    return (
      <p className="text-sm whitespace-pre-wrap break-words" data-testid={`dm-event-fallback-${eventId}`}>
        {fallbackText}
      </p>
    );
  }

  const start = event.start_time ? parseUtc(event.start_time) : null;
  const end = event.end_time ? parseUtc(event.end_time) : null;
  const visioLink = event.visio_link || '';
  const ownerId = event.user_id || '';
  const isBookable = event.type === 'public_event' || event.type === 'public_slot' || event.type === 'public_series';
  const isPast = end && end.getTime() < Date.now();

  return (
    <div
      className="rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 p-3 max-w-xs shadow-sm"
      data-testid={`dm-event-preview-${eventId}`}
    >
      {/* Header — date pill + title */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-12 h-14 bg-white rounded-lg shadow-sm flex flex-col items-center justify-center border border-purple-100">
          <span className="text-[10px] font-bold text-purple-600 uppercase">
            {start ? start.toLocaleDateString('fr-FR', { month: 'short' }) : '—'}
          </span>
          <span className="text-lg font-bold text-gray-900 leading-none">
            {start ? start.getDate() : '—'}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[9px] font-bold uppercase tracking-wider text-purple-600 block">
            Événement partagé
          </span>
          <h4 className="font-semibold text-gray-900 text-sm leading-tight" data-testid={`dm-event-title-${eventId}`}>
            {event.title}
          </h4>
          {start && (
            <p className="text-[11px] text-gray-500 mt-0.5">
              {start.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
              {' · '}
              {start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      </div>

      {event.description && (
        <p className="text-xs text-gray-600 mt-2 line-clamp-2">{event.description}</p>
      )}

      {/* CTAs — same set as Feed/Wall for consistency */}
      <div className="flex items-center flex-wrap gap-1.5 mt-3">
        {visioLink && !isPast && (
          <a
            href={visioLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-medium text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded-md"
            data-testid={`dm-event-join-${eventId}`}
          >
            <Video className="h-3 w-3" />Rejoindre
          </a>
        )}
        {isBookable && ownerId && !isPast && (
          <button
            type="button"
            onClick={() => navigate(`/event/${eventId}?book=1`)}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:opacity-90 px-2 py-1 rounded-md"
            data-testid={`dm-event-book-${eventId}`}
          >
            <Users className="h-3 w-3" />Réserver
          </button>
        )}
        {eventId && (
          <button
            type="button"
            onClick={() => navigate(`/event/${eventId}`)}
            className="inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 px-2 py-1 rounded-md"
            data-testid={`dm-event-view-${eventId}`}
          >
            <ExternalLink className="h-3 w-3" />Voir
          </button>
        )}
        {isPast && (
          <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">Terminé</span>
        )}
      </div>
    </div>
  );
};

/**
 * Parses a message content and returns either:
 *   - { kind: 'event', eventId, trailing }   when message starts with [EVENT_SHARE:<id>]
 *   - { kind: 'text', text }                  otherwise
 */
export const parseEventShareMessage = (content) => {
  if (!content || typeof content !== 'string') return { kind: 'text', text: content };
  const match = content.match(/^\[EVENT_SHARE:([^\]]+)\]\s*([\s\S]*)$/);
  if (!match) return { kind: 'text', text: content };
  return { kind: 'event', eventId: match[1].trim(), trailing: (match[2] || '').trim() };
};

export default MessageEventPreview;
